import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import CustomButton from 'components/button/CustomButton';
import UserSelect from 'components/form/UserSelect';
import { errorDefault } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 既に割当済みのユーザーID（重複追加不可・候補から除外） */
  assignedUserIds: string[];
  /** 割当確定時に選択されたユーザーIDを返す */
  onAssign: (userIds: string[]) => void;
  /** 親の割当処理中フラグ（二重送信抑止・ボタン無効化） */
  submitting?: boolean;
};

/** 課題をユーザーに一括割り当てするダイアログ（追加のみ・既割当は除外）。 */
const AssignUsersDialog: React.FC<Props> = ({
  open,
  onClose,
  assignedUserIds,
  onAssign,
  submitting = false,
}) => {
  const { users } = useAuth();
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) setSelectedUserIds([]);
  }, [open]);

  // 既割当ユーザーを候補から除外（重複追加不可）
  const assignable = React.useMemo(
    () => users.filter((u) => !assignedUserIds.includes(u.userId)),
    [users, assignedUserIds],
  );

  const handleAssign = () => {
    if (submitting) return;
    if (selectedUserIds.length === 0) {
      errorDefault('割り当てるユーザーを選択してください');
      return;
    }
    onAssign(selectedUserIds);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          課題をユーザーに割り当て
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            割り当ては追加のみです。一度割り当てると解除できません。
          </Alert>
          <UserSelect
            mode="multi"
            users={assignable}
            selectedUserIds={selectedUserIds}
            onChange={setSelectedUserIds}
            label="割り当てるユーザー"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={onClose}
          disabled={submitting}
        />
        <CustomButton
          label={`割り当て（${selectedUserIds.length}名）`}
          buttonVariant="primary"
          onClick={handleAssign}
          disabled={submitting}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersDialog;
